import { useEffect } from "react";

/**
 * useSeoHead — imperatively manages <title>, meta description, canonical link,
 * Open Graph / Twitter tags, and a JSON-LD structured-data script in the page
 * <head>. Client-only; since we're an SPA, crawlers get this after hydration
 * (Google, Bing, and most modern AI crawlers execute JS).
 *
 * On unmount we intentionally DO NOT restore defaults — the next screen's
 * useSeoHead will overwrite these nodes, and leaving stale tags in-between
 * navigations is better than a flash of <title>Pick2Play</title>.
 */
export default function useSeoHead({
  title,
  description,
  canonical,
  image,
  type = "website",
  jsonLd,
}) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (selector, attr, value, buildNode) => {
      if (value == null) return;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = buildNode();
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    if (description) {
      setMeta('meta[name="description"]', "content", description, () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "description");
        return m;
      });
    }
    if (canonical) {
      setMeta('link[rel="canonical"]', "href", canonical, () => {
        const l = document.createElement("link");
        l.setAttribute("rel", "canonical");
        return l;
      });
    }
    // Open Graph
    setMeta('meta[property="og:title"]', "content", title, () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:title");
      return m;
    });
    setMeta('meta[property="og:description"]', "content", description, () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:description");
      return m;
    });
    setMeta('meta[property="og:type"]', "content", type, () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:type");
      return m;
    });
    if (canonical) setMeta('meta[property="og:url"]', "content", canonical, () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:url");
      return m;
    });
    if (image) setMeta('meta[property="og:image"]', "content", image, () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:image");
      return m;
    });
    // Twitter summary card
    setMeta('meta[name="twitter:card"]', "content", image ? "summary_large_image" : "summary", () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "twitter:card");
      return m;
    });

    // JSON-LD — replace the existing one if present so we don't accumulate
    // stale structured data across navigations.
    const SCRIPT_ID = "pp-jsonld";
    document.getElementById(SCRIPT_ID)?.remove();
    if (jsonLd) {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.type = "application/ld+json";
      s.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
  }, [title, description, canonical, image, type, jsonLd]);
}
